import React from 'react';
import { ChevronLeft, CreditCard, Lock, Loader2, Calendar, Clock } from 'lucide-react';
import { useApp } from '../context/AppContext';

const PaymentView = () => {
  const {
    selectedProvider,
    paymentData,
    setPaymentData,
    bookingData,
    isProcessing,
    processPayment,
    setView,
    formatDate,
    calculateTotal
  } = useApp();
  
  const p = selectedProvider;
  if (!p) return null;
  
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => setView('booking')} className="flex items-center gap-2 text-slate-600 hover:text-teal-600 mb-6">
          <ChevronLeft className="w-5 h-5" />Back to Booking
        </button>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-emerald-500 p-6">
              <h1 className="text-2xl font-bold text-white">Payment Details</h1>
            </div>
            <div className="p-8 space-y-6">
              <div className="flex gap-4 mb-8">
                <button className="flex-1 p-4 border-2 border-teal-600 rounded-xl bg-teal-50">
                  <CreditCard className="w-6 h-6 text-teal-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-teal-700 text-center">Credit Card</p>
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Cardholder Name</label>
                <input 
                  type="text" 
                  value={paymentData.name} 
                  onChange={e => setPaymentData(prev => ({...prev, name: e.target.value}))} 
                  placeholder="John Smith" 
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Card Number</label>
                <input 
                  type="text" 
                  value={paymentData.cardNumber} 
                  onChange={e => setPaymentData(prev => ({...prev, cardNumber: e.target.value.replace(/\D/g,'').replace(/(\d{4})(?=\d)/g,'$1 ').slice(0,19)}))} 
                  placeholder="1234 5678 9012 3456" 
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Expiry</label>
                  <input 
                    type="text" 
                    value={paymentData.expiry} 
                    onChange={e => { 
                      let v = e.target.value.replace(/\D/g,''); 
                      if(v.length >= 2) v = v.slice(0,2)+'/'+v.slice(2,4); 
                      setPaymentData(prev => ({...prev, expiry: v})); 
                    }} 
                    placeholder="MM/YY" 
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">CVV</label>
                  <input 
                    type="text" 
                    value={paymentData.cvv} 
                    onChange={e => setPaymentData(prev => ({...prev, cvv: e.target.value.replace(/\D/g,'').slice(0,4)}))} 
                    placeholder="123" 
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" 
                  />
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl flex items-start gap-4">
                <Lock className="w-6 h-6 text-teal-600" />
                <div>
                  <p className="font-medium text-slate-700">Secure Payment</p>
                  <p className="text-sm text-slate-500">Your payment is encrypted and secure.</p>
                </div>
              </div>
              <button 
                onClick={processPayment} 
                disabled={isProcessing || !paymentData.cardNumber || !paymentData.expiry || !paymentData.cvv || !paymentData.name} 
                className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-3 ${!isProcessing && paymentData.cardNumber && paymentData.expiry && paymentData.cvv && paymentData.name ? 'bg-gradient-to-r from-teal-600 to-emerald-500 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
              >
                {isProcessing ? <><Loader2 className="w-5 h-5 animate-spin" />Processing...</> : <><Lock className="w-5 h-5" />Pay ${calculateTotal()}</>}
              </button>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Summary</h3>
              <div className="flex items-center gap-4 mb-6 pb-6 border-b">
                <img src={p.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name || 'Provider')}&background=0d9488&color=fff`} alt={p.name} className="w-16 h-16 rounded-xl object-cover" />
                <div>
                  <p className="font-semibold text-slate-800">{p.name || `${p.firstName} ${p.lastName}`}</p>
                  <p className="text-slate-500 text-sm">{p.providerType?.name || p.providerType}</p>
                </div>
              </div>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-slate-600">
                  <Calendar className="w-5 h-5 text-teal-600" />
                  {formatDate(bookingData.date)}
                </div>
                <div className="flex items-center gap-3 text-slate-600">
                  <Clock className="w-5 h-5 text-teal-600" />
                  {bookingData.time} • {bookingData.duration} hrs
                </div>
              </div>
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal</span>
                  <span>${((p.hourlyRate || 0) * bookingData.duration).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Service Fee</span>
                  <span>${((p.hourlyRate || 0) * bookingData.duration * 0.05).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-slate-800 pt-4 border-t">
                  <span>Total</span>
                  <span>${calculateTotal()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentView;
